import {ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Feature} from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import {Geometry} from 'ol/geom';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import {EMPTY, Subject, catchError, distinctUntilChanged, forkJoin, map, startWith, switchMap, take, tap} from 'rxjs';
import {AppService} from '../../../../services/app.service';
import {AverageCurve, AverageCurvePeriod, EdgarPipelineService} from '../../../../services/edgar-pipeline.service';
import {MapService} from '../../../../services/map.service';
import {selectCounties} from '../../../../state/location/location.reducer';
import {BaseUILayer} from '../../../classes/layers/base-ui-layer';
import {CountySrcPopupComponent} from '../../../layer-popups/county-src-popup/county-src-popup.component';
import {CountyCurveCompareDialogComponent} from '../../../modals/county-curve-compare-dialog/county-curve-compare-dialog.component';
import {LayerHeaderComponent} from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-county-src-layer',
  imports: [LayerHeaderComponent, MatButtonModule, MatFormFieldModule, MatSelectModule, ReactiveFormsModule],
  templateUrl: './county-src-layer.component.html',
  standalone: true,
  styleUrl: './county-src-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountySrcLayerComponent extends BaseUILayer<BaseLayer> {
  private readonly appService = inject(AppService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly pipeline = inject(EdgarPipelineService);
  private readonly loadRequests = new Subject<void>();

  protected readonly periods = signal<AverageCurvePeriod[]>([]);
  protected readonly loading = signal(false);
  protected readonly availableCount = signal(0);
  protected readonly curves = signal<AverageCurve[]>([]);
  protected readonly filterGroup = new FormGroup({
    datasetKey: new FormControl<string>('', {nonNullable: true}),
  });

  public constructor() {
    super();
    this.loadRequests.pipe(
      tap(() => this.loading.set(true)),
      switchMap(() => {
        const states = this.appService.selectedStates();
        const stateIds = states.map(state => state.id);
        const stateAbbreviations = states.map(state => state.abbreviation);
        return forkJoin({
          curves: this.pipeline.getAverageCurves(
            this.filterGroup.controls.datasetKey.value || undefined,
            stateAbbreviations,
          ),
          counties: this.store.select(selectCounties).pipe(take(1)),
        }).pipe(
          map(({curves, counties}) => {
            this.curves.set(curves);
            const curveByFips = new Map<string, AverageCurve>(curves.map(curve => [curve.fipsCode, curve]));
            this.availableCount.set(curves.length);
            return counties
              .filter(county => stateIds.length === 0 || stateIds.includes(county.stateId))
              .map(county => ({
                type: 'Feature',
                geometry: JSON.parse(county.boundary) as unknown,
                properties: {
                  ...county,
                  popoverComponent: CountySrcPopupComponent,
                  curveData: curveByFips.get(county.geoid) ?? null,
                  hasData: curveByFips.has(county.geoid),
                },
              }));
          }),
          catchError(() => {
            this.loading.set(false);
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(counties => {
      this.loading.set(false);
      const features = new GeoJSON().readFeatures({type: 'FeatureCollection', features: counties});
      const existing = this.mapLayer().renderedLayer as VectorLayer<VectorSource> | undefined;
      if (existing && this.mapService.isLayerOnMap(existing)) {
        existing.getSource()?.clear();
        existing.getSource()?.addFeatures(features as Feature<Geometry>[]);
        return;
      }
      const layer = new VectorLayer({
        source: new VectorSource({features: features as Feature<Geometry>[], attributions: this.mapLayer().buildAttribution()}),
        properties: {name: this.mapLayer().displayName},
        opacity: this.opacity / 100,
        style: feature => new Style({
          stroke: new Stroke({color: '#182230', width: 0.8}),
          fill: new Fill({color: feature.get('hasData') ? '#4f7dd6' : '#d7dce3'}),
        }),
      });
      this.mapLayer().renderedLayer = layer;
      MapService.getMapRef()?.addLayer(layer);
    });
    this.pipeline.getCurvePeriods().pipe(takeUntilDestroyed()).subscribe(periods => this.periods.set(periods));
    this.filterGroup.controls.datasetKey.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.checked) this.getData();
    });
    effect(() => {
      this.appService.selectedStates();
      if (this.checked) this.getData();
    });
  }

  public periodLabel(period: AverageCurvePeriod): string {
    if (period.periodKind === 'all') {
      return `All years (${period.coverageStartDate.format('YYYY-MM-DD')} – ${period.coverageEndDate.format('YYYY-MM-DD')})`;
    }
    return `${period.periodYear} annual curve`;
  }

  public allPeriodLabel(): string {
    const period = this.periods().find(item => item.periodKind === 'all');
    return period ? this.periodLabel(period) : 'All available years';
  }

  protected openCompare(): void {
    const curves = this.curves();
    if (curves.length === 0) return;
    const datasetKey = this.filterGroup.controls.datasetKey.value;
    const period = this.periods().find(item => item.datasetKey === datasetKey) ?? this.periods().find(item => item.periodKind === 'all');
    this.dialog.open(CountyCurveCompareDialogComponent, {
      autoFocus: false,
      maxWidth: '98vw',
      panelClass: 'county-curve-compare-dialog',
      data: {
        curves,
        periods: this.periods(),
        states: this.appService.selectedStates().map(state => state.abbreviation),
        selectedDatasetKey: period?.datasetKey ?? curves[0]?.datasetKey ?? '',
        periodLabel: period ? this.periodLabel(period) : this.allPeriodLabel(),
      },
    });
  }

  public override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (checked) this.getData();
    else if (this.mapLayer().renderedLayer) MapService.getMapRef()?.removeLayer(this.mapLayer().renderedLayer);
  }

  public override onDestroy(): void {
    if (this.mapLayer().renderedLayer) MapService.getMapRef()?.removeLayer(this.mapLayer().renderedLayer);
  }

  public override changeOpacity(opacity: number): void {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource> | undefined)?.setOpacity(opacity / 100);
  }

  private getData(): void {
    this.loadRequests.next();
  }
}
